// SellScrapPage with section differentiation matching BuyerLandingPage

import React, { useState } from "react";
import { Box, Typography, Container, Grid, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaStore,
  FaTools,
  FaTags,
  FaHandshake,
  FaQuoteLeft,
  FaPlay,
  FaUsers,
  FaLock,
  FaChartLine,
  FaBell,
  FaMoneyBillWave,
  FaHeadset,
  FaShieldAlt, // Secure Payments
  FaTruck, // Logistics Support
  FaLeaf, // Sustainability Impact
  FaLaptopCode, // Easy-to-Use Platform
} from "react-icons/fa";

import Seo from "../components/Seo";

import Breadcrumb from "../components/Breadcrumb";
import { generateBreadcrumbs } from "../utils/breadcrumbs";
import KycBanner from "../components/common/KycBanner";

import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser, setCredentials } from "../features/auth/authSlice";
import VerifySwitchRoleModal from "../components/buyer/VerifySwitchRoleModal";
import { fetchUserAddresses, switchRole } from "../features/user/userSlice";
import Loader from "../components/common/Loader";

const themeColors = {
  primary: "#F5B800",
  secondary: "#334155",
  background: "#f8fafd",
  cardBackground: "#ffffff",
  shadow: "rgba(0, 0, 0, 0.08)",
  ctaBackground: "#2d3748",
};

const SellScrapPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token, role, roles, user } = useSelector((state) => state.auth);
  // const { roles } = useSelector((state) => state.user);
  const isLoggedIn = Boolean(token);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const handleRoleSwitch = async (targetRole) => {
    setIsSwitching(true);
    try {
      const result = await dispatch(switchRole({ role: targetRole })).unwrap();

      // If switching to seller, fetch address count
      if (targetRole === "seller") {
        try {
          const addressRes = await dispatch(fetchUserAddresses()).unwrap();
          const addressList = addressRes?.data || addressRes;
          const addressCount = Array.isArray(addressList)
            ? addressList.length
            : 0;

          // Re-sync: merge freshUser ON TOP of existing user to preserve verification flags
          const freshUser = result?.data?.user || result?.user;
          const updatedRoles = roles?.includes("seller")
            ? roles
            : [...(roles || []), "seller"];
          const mergedUser = {
            ...user,
            ...(freshUser || {}),
            addresses_count: addressCount,
            roles: updatedRoles,
          };

          dispatch(
            setCredentials({
              user: mergedUser,
              role: targetRole,
              roles: updatedRoles,
            }),
          );
        } catch (addrErr) {
          console.error("Error fetching addresses during switch:", addrErr);
        }
      }

      // Navigate to dashboard after successful switch
      if (targetRole === "seller") {
        navigate("/seller/dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Role switch failed:", error);
    } finally {
      setIsSwitching(false);
    }
  };

  // Handler for modal confirmation
  const handleConfirmRoleSwitch = () => {
    handleRoleSwitch("seller");
  };

  const handleSellerRoleSwitch = async () => {
    console.log("🔍 Debug Info:", { isLoggedIn, role, roles });
    console.log("🔍 Roles array content:", roles); // ✅ Add this to see what's inside
    console.log("🔍 First role:", roles?.[0]);
    if (!isLoggedIn) {
      navigate("/login", { state: { role: "seller" } });
      return;
    }

    // Already a seller → go to dashboard
    if (role === "seller" || roles?.includes("seller")) {
      console.log("✅ Navigating to seller dashboard");
      navigate("/seller/dashboard");
      return;
    }

    // User logged in but not registered as a seller
    if (!roles?.includes("seller")) {
      console.log("❌ Showing modal because no seller role");
      setShowConfirmModal(true); // open modal instead of window.confirm

      return;
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.6 },
    }),
  };

  const steps = [
    {
      icon: <FaStore size={32} color={themeColors.primary} />,
      title: "Register & List",
      description:
        "Sign up, verify your profile, and create listings with details and photos.",
    },
    {
      icon: <FaTools size={32} color={themeColors.primary} />,
      title: "Get Offers",
      description:
        "Receive bids or buyer requests, review them, and pick the best deal.",
    },
    {
      icon: <FaTags size={32} color={themeColors.primary} />,
      title: "Secure the Deal",
      description:
        "Confirm the order with funds held safely in escrow until release.",
    },
    {
      icon: <FaHandshake size={32} color={themeColors.primary} />,
      title: "Pickup & Weighing",
      description:
        "Arrange collection, ensure fair weighing, and complete the transaction.",
    },
  ];

  const testimonials = [
    {
      name: "John D.",
      content:
        "ScrapTrade helped me reach more buyers than ever before. It’s easy to use and reliable.",
    },
    {
      name: "Samantha R.",
      content:
        "I was able to sell my scrap faster and at better prices. The platform is super efficient!",
    },
    {
      name: "Mark W.",
      content:
        "Excellent customer support and a great network of buyers. Highly recommend to any seller.",
    },
  ];

  const features = [
    {
      title: "Global Buyer Network",
      desc: "Connect with a wide pool of verified buyers.",
      icon: <FaUsers size={60} />,
    },
    {
      title: "Competitive Pricing",
      desc: "Earn more through bids and reverse bidding.",
      icon: <FaTags size={60} />,
    },
    {
      title: "Secure Payments",
      desc: "Escrow-protected transactions ensure guaranteed payouts.",
      icon: <FaShieldAlt size={60} />,
    },
    {
      title: "Logistics Support",
      desc: "Schedule smooth pickups and deliveries with ease.",
      icon: <FaTruck size={60} />,
    },
    {
      title: "Sustainability Impact",
      desc: "Promote recycling and reduce environmental waste.",
      icon: <FaLeaf size={60} />,
    },
    {
      title: "Easy-to-Use Platform",
      desc: "Simple tools make listing and selling effortless.",
      icon: <FaLaptopCode size={60} />,
    },
  ];

  const breadcrumbs = generateBreadcrumbs({ pageType: "sell" });

  return (
    <>
      {isSwitching && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Loader />
        </Box>
      )}
      <Seo
        title="Sell Scrap Online | ScrapTrade Marketplace"
        description="Turn your scrap into value. List scrap online with ScrapTrade, reach verified buyers, get real-time prices, and enjoy safe, fast transactions."
        url="https://scraptrade.com.au/sell"
        canonical="https://scraptrade.com.au/sell"
        image="https://scraptrade.com.au/assets/og/scrap.jpg"
        breadcrumbs={breadcrumbs}
        extraSchema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Sell Scrap | ScrapTrade",
          description:
            "ScrapTrade helps sellers list scrap, reach verified buyers, and secure payments.",
          mainEntity: {
            "@type": "HowTo",
            name: "How to Sell Scrap on ScrapTrade",
            step: [
              {
                "@type": "HowToStep",
                name: "Create a Seller Account",
                text: "Register and set up your verified seller profile.",
              },
              {
                "@type": "HowToStep",
                name: "List Your Scrap",
                text: "Add photos, quantity, and pricing for your scrap.",
              },
              {
                "@type": "HowToStep",
                name: "Start Selling",
                text: "Connect with verified buyers.",
              },
              {
                "@type": "HowToStep",
                name: "Get Paid",
                text: "Negotiate, confirm deals, and receive secure payments.",
              },
            ],
          },
          potentialAction: [
            {
              "@type": "RegisterAction",
              target: "https://scraptrade.com.au/register?type=seller",
              name: "Create Free Seller Account",
            },
          ],
          review: [
            {
              "@type": "Review",
              reviewBody:
                "ScrapTrade helped me reach more buyers than ever before. It’s easy to use and reliable.",
              author: { "@type": "Person", name: "John D." },
              reviewRating: { "@type": "Rating", ratingValue: "5" },
            },
            {
              "@type": "Review",
              reviewBody:
                "I was able to sell my scrap faster and at better prices. The platform is super efficient!",
              author: { "@type": "Person", name: "Samantha R." },
              reviewRating: { "@type": "Rating", ratingValue: "5" },
            },
            {
              "@type": "Review",
              reviewBody:
                "Excellent customer support and a great network of buyers. Highly recommend to any seller.",
              author: { "@type": "Person", name: "Mark W." },
              reviewRating: { "@type": "Rating", ratingValue: "5" },
            },
          ],
          faq: {
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How do I sell scrap on ScrapTrade?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Simply create a free seller account, list your scrap, and start connecting with verified buyers.",
                },
              },
              {
                "@type": "Question",
                name: "Is it safe to sell scrap on ScrapTrade?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes, ScrapTrade ensures secure payments, verified buyers, and dedicated seller support.",
                },
              },
              {
                "@type": "Question",
                name: "Does ScrapTrade charge sellers?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Creating a seller account is free. Some premium features may have additional costs.",
                },
              },
            ],
          },
        }}
      />
      <Box
        sx={{
          fontFamily: "Roboto, sans-serif",
          backgroundColor: themeColors.background,
        }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            py: 12,
            textAlign: "center",
            background: `linear-gradient(145deg, ${themeColors.background}, #e2e8f0)`,
          }}
        >
          <Container>
            <motion.div
              initial={{ opacity: 0, y: -40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h3"
                fontWeight="bold"
                gutterBottom
                sx={{ color: themeColors.secondary }}
              >
                Sell Scrap Online with ScrapTrade
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                ScrapTrade connects you with verified buyers across global.
                Whether you’re an individual, recycler, or business, our
                platform makes selling scrap simple, transparent, and
                profitable.
              </Typography>
              <motion.div whileHover={{ scale: 1.05 }}>
                <Button
                  // component={Link}
                  // to={
                  //   isLoggedIn ? "/seller/dashboard" : "/register?role=seller"
                  // }
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: themeColors.primary,
                    fontWeight: "bold",
                    color: "#fff",
                    boxShadow: `0 0 10px ${themeColors.primary}`,
                    px: 4,
                    "&:hover": {
                      backgroundColor: "#d49f00",
                    },
                  }}
                  onClick={handleSellerRoleSwitch}
                >
                  {isLoggedIn ? "Go to Dashboard" : "Join as Seller"}
                </Button>
              </motion.div>
            </motion.div>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ mt: 3, mb: 1 }}>
          <Breadcrumb items={breadcrumbs} />
        </Container>
        <KycBanner />

        {/* Steps Section */}
        <Box sx={{ py: 10, overflowX: "auto" }}>
          <Container>
            <Typography
              variant="h4"
              fontWeight="bold"
              textAlign="center"
              gutterBottom
              sx={{ color: themeColors.secondary }}
            >
              How to Start Selling on ScrapTrade
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 4,
                mt: 4,
                minWidth: "100%",
                px: 2,
              }}
            >
              {steps.map((step, idx) => (
                <motion.div
                  key={idx}
                  variants={fadeInUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={idx}
                  style={{ flex: "0 0 auto", width: 250 }} // fixed width for consistency
                >
                  <Box
                    sx={{
                      aspectRatio: "1 / 1", // 1:1 square
                      backgroundColor: themeColors.cardBackground,
                      borderRadius: 3,
                      p: 3,
                      boxShadow: `0 0 15px ${themeColors.shadow}`,
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: `0 0 25px ${themeColors.primary}`,
                      },
                    }}
                  >
                    <Box sx={{ mb: 2 }}>{step.icon}</Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {step.description}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 6,
              }}
            >
              <Link to="/blog" style={{ textDecoration: "none" }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    background: themeColors.primary,
                    color: "#fff",
                    px: 8,
                    py: 2.5,
                    borderRadius: 2,
                    boxShadow: `0 0 12px ${themeColors.primary}`,
                    textTransform: "none",
                    fontWeight: "bold",
                    gap: 1.5,
                    "&:hover": {
                      background: themeColors.primary,
                      boxShadow: `0 0 20px ${themeColors.primary}`,
                    },
                  }}
                  startIcon={<FaPlay />}
                >
                  Watch Demo
                </Button>
              </Link>
            </Box>
          </Container>
        </Box>

        {/* Why Choose ScrapTrade Section */}
        <Box sx={{ py: 10, backgroundColor: "#f1f5f9" }}>
          <Container>
            <Typography
              variant="h4"
              fontWeight="bold"
              textAlign="center"
              gutterBottom
              sx={{ color: themeColors.secondary }}
            >
              Why Sell Scrap on ScrapTrade?
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 6 }}
            >
              We provide sellers with the tools, network, and support to grow
              their scrap business efficiently and securely.
            </Typography>

            <Grid container spacing={4} justifyContent="center">
              {features.map((feature, idx) => (
                <Grid item key={idx}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: idx * 0.15 }}
                    viewport={{ once: true }}
                  >
                    <Box
                      sx={{
                        p: 3,
                        width: 280,
                        height: 280,
                        borderRadius: 10,
                        backdropFilter: "blur(10px)",
                        background: `linear-gradient(135deg, ${themeColors.cardBackground}88, ${themeColors.primary}11)`,
                        border: `1px solid ${themeColors.primary}33`,
                        boxShadow: `0 0 20px ${themeColors.shadow}`,
                        transition: "all 0.3s ease",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        "&:hover": {
                          boxShadow: `0 0 30px ${themeColors.primary}`,
                          transform: "translateY(-6px)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          color: themeColors.primary,
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "transform 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                        sx={{ color: themeColors.secondary }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: 13 }}
                      >
                        {feature.desc}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Testimonials Section */}
        <Box
          sx={{
            py: 10,
            background: `linear-gradient(to right, #f8fafc, #e2e8f0)`,
          }}
        >
          <Container>
            <Typography
              variant="h4"
              fontWeight="bold"
              textAlign="center"
              gutterBottom
              sx={{ color: themeColors.secondary }}
            >
              What Sellers Say About Us
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center", // 👈 Center the cards
                flexWrap: "wrap",
                gap: 4,
                mt: 4,
              }}
            >
              {testimonials.map((t, idx) => (
                <motion.div
                  key={idx}
                  variants={fadeInUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={idx}
                  style={{ flex: "0 0 auto", width: 250 }}
                >
                  <Box
                    sx={{
                      aspectRatio: "1 / 1",
                      borderRadius: 3,
                      p: 3,
                      background: `linear-gradient(135deg, ${themeColors.cardBackground}, ${themeColors.primary}10)`,
                      boxShadow: `0 0 15px ${themeColors.shadow}`,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: `0 0 25px ${themeColors.primary}`,
                      },
                    }}
                  >
                    <Box sx={{ color: themeColors.primary, mb: 1 }}>
                      <FaQuoteLeft fontSize="large" />
                    </Box>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ flexGrow: 1 }}
                    >
                      "{t.content}"
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ mt: 2 }}
                    >
                      {t.name}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            py: 10,
            backgroundColor: themeColors.ctaBackground,
            textAlign: "center",
          }}
        >
          <Container>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <Typography
                variant="h4"
                fontWeight="bold"
                gutterBottom
                sx={{ color: "#fff" }}
              >
                Ready to Turn Scrap Into Profit?
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, color: "#e0e0e0" }}>
                Join ScrapTrade today — list your materials, connect with
                verified buyers, and get paid securely.
              </Typography>
              <motion.div whileHover={{ scale: 1.05 }}>
                <Button
                  // component={Link}
                  // to={
                  //   isLoggedIn ? "/seller/dashboard" : "/register?role=seller"
                  // }

                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: themeColors.primary,
                    fontWeight: "bold",
                    px: 4,
                    color: "#fff",
                    boxShadow: `0 0 10px ${themeColors.primary}`,
                    "&:hover": {
                      backgroundColor: "#d49f00",
                    },
                  }}
                  onClick={handleSellerRoleSwitch}
                >
                  {isLoggedIn ? "Go to Dashboard" : "Start Selling Now"}
                </Button>
              </motion.div>
            </motion.div>
          </Container>
        </Box>
      </Box>

      {/* <VerifySwitchRoleModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmRoleSwitch}
        title="Activate Seller Account"
        description="You are about to activate your seller account."
        confirmText="Activate"
        cancelText="Cancel"
      /> */}

      <VerifySwitchRoleModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Activate Seller Account"
        description="You are about to activate your seller account."
        confirmText="Activate"
        cancelText="Cancel"
        onConfirm={handleConfirmRoleSwitch}
      />
    </>
  );
};

export default SellScrapPage;
